/**
 * Abgelehnte Belege (status 'rejected'): bleiben GoBD-konform archiviert, werden aber nicht gebucht.
 * Der Grund steht in invoice_documents.rejection_reason.
 */
export const INVOICE_REJECTION_REASONS = [
  'not_invoice',
  'duplicate',
  'wrong_recipient',
  'reclassified',
  'other',
] as const;

export type InvoiceRejectionReason = (typeof INVOICE_REJECTION_REASONS)[number];

export const INVOICE_REJECTION_REASON_LABEL: Record<InvoiceRejectionReason, string> = {
  not_invoice: 'Keine Rechnung',
  duplicate: 'Dublette',
  wrong_recipient: 'Falscher Empfänger',
  reclassified: 'Umgewandelt',
  other: 'Sonstiger Grund',
};

/** Vom Nutzer wählbar; 'reclassified' setzt nur die Umwandlung. */
export const USER_SELECTABLE_REJECTION_REASONS: InvoiceRejectionReason[] = [
  'not_invoice',
  'duplicate',
  'wrong_recipient',
  'other',
];

export function normalizeRejectionReason(raw: unknown): InvoiceRejectionReason | null {
  const value = String(raw ?? '').trim();
  return (INVOICE_REJECTION_REASONS as readonly string[]).includes(value) ? (value as InvoiceRejectionReason) : null;
}

/** Was die Auslese statt einer Rechnung erkannt hat. */
export const NON_INVOICE_KINDS = [
  'delivery_note',
  'order_confirmation',
  'quote',
  'dunning',
  'account_statement',
  'advertising',
  'other_document',
] as const;

export type NonInvoiceKind = (typeof NON_INVOICE_KINDS)[number];

export const NON_INVOICE_KIND_LABEL: Record<NonInvoiceKind, string> = {
  delivery_note: 'Lieferschein',
  order_confirmation: 'Auftragsbestätigung',
  quote: 'Angebot',
  dunning: 'Mahnung / Zahlungserinnerung',
  account_statement: 'Kontoauszug / Saldenbestätigung',
  advertising: 'Werbung / Newsletter',
  other_document: 'Sonstiges Dokument',
};

export function normalizeNonInvoiceKind(raw: unknown): NonInvoiceKind | null {
  const value = String(raw ?? '').trim();
  return (NON_INVOICE_KINDS as readonly string[]).includes(value) ? (value as NonInvoiceKind) : null;
}

export function nonInvoiceKindFromExtractionJson(json: unknown): NonInvoiceKind | null {
  if (!json || typeof json !== 'object') return null;
  return normalizeNonInvoiceKind((json as { nonInvoiceKind?: unknown }).nonInvoiceKind);
}

/** Status nach einer Statusänderung: Grund nur bei 'rejected', sonst leeren. */
export function rejectionReasonForStatus(
  nextStatus: string,
  requested: unknown,
  existing: unknown,
): InvoiceRejectionReason | null {
  if (nextStatus !== 'rejected') return null;
  return normalizeRejectionReason(requested) ?? normalizeRejectionReason(existing) ?? 'other';
}
